import { createFileRoute } from '@tanstack/react-router'
import { useState, useRef, useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  UploadIcon,
  FileSpreadsheetIcon,
  CheckCircleIcon,
  XCircleIcon,
  AlertTriangleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  LoaderIcon,
  DatabaseIcon,
  Table2Icon,
  UsersIcon,
  DownloadIcon,
  HistoryIcon,
  GlobeIcon,
} from 'lucide-react'
import {
  uploadImportFileApi,
  executeUserImportApi,
  getImportRunsApi,
  getImportRefsApi,
  type ImportRun,
} from '@/lib/api'
import { cn } from '@/lib/utils'

// ── User field interface from the API ───────────────────────────────────

interface UserField {
  key: string
  label: string
  required: boolean
  type: string
  description: string
  options?: string[]
}

interface ImportRefs {
  roles: Array<{ value: string; label: string }>
  organizations: Array<{ org_id: number; org_name: string }>
}

// ── Route definition ────────────────────────────────────────────────────

export const Route = createFileRoute('/dashboard/admin/user-import')({
  component: UserImportPage,
})

// ── Main component ──────────────────────────────────────────────────────

function UserImportPage() {
  const [step, setStep] = useState(0) // 0=upload, 1=mapping, 2=global-values, 3=execute, 4=results
  const [uploadId, setUploadId] = useState<string | null>(null)
  const [columns, setColumns] = useState<string[]>([])
  const [previewRows, setPreviewRows] = useState<Record<string, string>[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [userFields, setUserFields] = useState<UserField[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [isUploading, setIsUploading] = useState(false)
  const [isExecuting, setIsExecuting] = useState(false)
  const [importResult, setImportResult] = useState<{
    total_rows: number
    inserted: number
    skipped: number
    errors: number
    error_details: Array<{ row: number; error: string }>
  } | null>(null)
  const [importRuns, setImportRuns] = useState<ImportRun[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  // ── Global values state ─────────────────────────────────────────────
  const [importRefs, setImportRefs] = useState<ImportRefs | null>(null)
  const [isLoadingRefs, setIsLoadingRefs] = useState(false)
  const [globalRole, setGlobalRole] = useState<string>('')
  const [globalOrgId, setGlobalOrgId] = useState<number | ''>('')

  // Fetch import history
  useEffect(() => {
    getImportRunsApi({ limit: 5 })
      .then(res => setImportRuns(res.data))
      .catch(() => {})
  }, [])

  // Fetch refs when entering the global-values step
  const fetchRefs = useCallback(async () => {
    if (importRefs) return // already loaded
    setIsLoadingRefs(true)
    try {
      const refs = await getImportRefsApi()
      setImportRefs(refs)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load reference data'
      toast.error(msg)
    } finally {
      setIsLoadingRefs(false)
    }
  }, [importRefs])

  // When step becomes 2, load refs
  useEffect(() => {
    if (step === 2) {
      fetchRefs()
    }
  }, [step, fetchRefs])

  // Reset everything
  const resetImport = useCallback(() => {
    setStep(0)
    setUploadId(null)
    setColumns([])
    setPreviewRows([])
    setTotalRows(0)
    setUserFields([])
    setMapping({})
    setImportResult(null)
    setGlobalRole('')
    setGlobalOrgId('')
    setImportRefs(null)
  }, [])

  // ── Step 1: Handle file upload ──────────────────────────────────────

  const handleFile = useCallback(async (file: File) => {
    // Validate extension
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!ext || !['csv', 'xls', 'xlsx'].includes(ext)) {
      toast.error('Please upload a CSV or Excel file (.csv, .xls, .xlsx)')
      return
    }

    setIsUploading(true)
    try {
      const result = await uploadImportFileApi(file)
      setColumns(result.columns)
      setPreviewRows(result.previewRows)
      setTotalRows(result.totalRows)
      setUploadId(result.uploadId)
      setUserFields(result.userFields)

      // Pre-populate mapping with auto-detection (heuristic match)
      const autoMapping: Record<string, string> = {}
      for (const col of result.columns) {
        const colLower = col.toLowerCase().replace(/[\s_-]/g, '')
        const match = result.userFields.find(f => {
          const keyLower = f.key.toLowerCase().replace(/[\s_-]/g, '')
          return colLower === keyLower ||
            colLower.includes(keyLower) ||
            keyLower.includes(colLower)
        })
        if (match) {
          autoMapping[col] = match.key
        }
      }
      setMapping(autoMapping)
      setStep(1)
      toast.success(`File parsed: ${result.totalRows.toLocaleString()} rows found`)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to parse file'
      toast.error(msg)
    } finally {
      setIsUploading(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  // ── Step 2: Mapping update ──────────────────────────────────────────

  const setMappingForColumn = useCallback((csvColumn: string, userField: string) => {
    setMapping(prev => {
      const next = { ...prev }
      // If user picks an empty value, clear the mapping
      if (!userField) {
        delete next[csvColumn]
      } else {
        // Remove any other column mapping to the same user field
        for (const [col, field] of Object.entries(next)) {
          if (field === userField && col !== csvColumn) {
            delete next[col]
          }
        }
        next[csvColumn] = userField
      }
      return next
    })
  }, [])

  // ── Step 3: Global values ───────────────────────────────────────────

  const handleApplyGlobalValues = useCallback(() => {
    const hasRole = globalRole !== ''
    const hasOrg = globalOrgId !== ''

    if (!hasRole && !hasOrg) {
      toast.info('No global values set — continuing without overrides.')
    } else {
      const parts: string[] = []
      if (hasRole) {
        const label = importRefs?.roles.find(r => r.value === globalRole)?.label ?? globalRole
        parts.push(`Role: ${label}`)
      }
      if (hasOrg) {
        const name = importRefs?.organizations.find(o => o.org_id === globalOrgId)?.org_name ?? `Org #${globalOrgId}`
        parts.push(`Org: ${name}`)
      }
      toast.success(`Global values applied: ${parts.join(', ')}`)
    }
    setStep(3)
  }, [globalRole, globalOrgId, importRefs])

  // ── Step 4: Execute import ──────────────────────────────────────────

  const handleExecute = useCallback(async () => {
    if (!uploadId) return

    // Validate required fields are mapped
    const mappedFields = Object.values(mapping)
    const missingRequired = userFields
      .filter(f => f.required && !mappedFields.includes(f.key))
      .map(f => f.label)

    if (missingRequired.length > 0) {
      toast.error(`Required fields not mapped: ${missingRequired.join(', ')}`)
      return
    }

    setIsExecuting(true)
    try {
      // Build globalValues object from the UI state
      const globalValues: { role?: string; org_id?: number } = {}
      if (globalRole) globalValues.role = globalRole
      if (globalOrgId !== '') globalValues.org_id = globalOrgId as number

      const result = await executeUserImportApi(uploadId, mapping, globalValues)
      setImportResult({
        total_rows: result.total_rows,
        inserted: result.inserted,
        skipped: result.skipped,
        errors: result.errors,
        error_details: result.error_details,
      })
      setStep(4)

      // Refresh history
      getImportRunsApi({ limit: 5 })
        .then(res => setImportRuns(res.data))
        .catch(() => {})

      if (result.errors === 0 && result.skipped === 0) {
        toast.success(`Successfully imported ${result.inserted.toLocaleString()} users`)
      } else {
        toast.info(
          `Import complete: ${result.inserted} inserted, ` +
          `${result.skipped} skipped, ${result.errors} errors`
        )
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Import failed'
      toast.error(msg)
    } finally {
      setIsExecuting(false)
    }
  }, [uploadId, mapping, userFields, globalRole, globalOrgId])

  // ── Determine whether global values are active ──────────────────────

  const hasGlobalValues = globalRole !== '' || globalOrgId !== ''

  // ── Render based on step ────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Bulk User Import</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Import users in bulk from a CSV or Excel file
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
          >
            <HistoryIcon className="mr-1.5 size-4" />
            History
          </Button>
          {step > 0 && (
            <Button variant="ghost" size="sm" onClick={resetImport}>
              <XCircleIcon className="mr-1.5 size-4" />
              New import
            </Button>
          )}
        </div>
      </div>

      {/* ── Step indicator ──────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        {[
          { num: 0, label: 'Upload', icon: UploadIcon },
          { num: 1, label: 'Map fields', icon: Table2Icon },
          { num: 2, label: 'Global values', icon: GlobeIcon },
          { num: 3, label: 'Execute', icon: DatabaseIcon },
          { num: 4, label: 'Results', icon: CheckCircleIcon },
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-2">
            {i > 0 && <div className="h-px w-8 bg-border" />}
            <div
              className={cn(
                'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                step === s.num
                  ? 'bg-primary text-primary-foreground'
                  : step > s.num
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                    : 'bg-muted text-muted-foreground',
              )}
            >
              <s.icon className="size-3.5" />
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* ── Import History ──────────────────────────────────────────── */}
      {showHistory && (
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold">Recent Imports</h3>
          {importRuns.length === 0 ? (
            <p className="text-xs text-muted-foreground">No import history yet.</p>
          ) : (
            <div className="space-y-2">
              {importRuns.map(run => (
                <div key={run.import_run_id} className="flex items-center justify-between rounded-lg border bg-background px-3 py-2 text-xs">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheetIcon className="size-4 text-muted-foreground" />
                    <span className="font-medium">{run.original_filename}</span>
                    <span className="text-muted-foreground">
                      {run.inserted_rows}/{run.total_rows} inserted
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'rounded-full px-2 py-0.5 text-[10px] font-medium',
                      run.status === 'completed' ? 'bg-green-500/10 text-green-600 dark:text-green-400' :
                      run.status === 'processing' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' :
                      run.status === 'failed' ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                      'bg-muted text-muted-foreground'
                    )}>
                      {run.status}
                    </span>
                    <span className="text-muted-foreground">
                      {new Date(run.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Step 0: Upload ──────────────────────────────────────────── */}
      {step === 0 && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 transition-all',
            dragOver
              ? 'border-[var(--brand-mint)] bg-[var(--brand-mint)]/5'
              : 'border-border hover:border-muted-foreground/30 hover:bg-muted/30',
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            className="hidden"
            onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
          />

          {isUploading ? (
            <div className="flex flex-col items-center gap-3">
              <LoaderIcon className="size-10 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Parsing file...</p>
            </div>
          ) : (
            <>
              <div className="mb-4 rounded-full bg-muted p-4">
                <UploadIcon className="size-8 text-muted-foreground" />
              </div>
              <p className="mb-1 text-base font-semibold">
                Drop your file here or click to browse
              </p>
              <p className="mb-4 text-xs text-muted-foreground">
                Supports CSV, XLS, and XLSX files (up to 50 MB)
              </p>

              <div className="flex items-center gap-6 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <CheckCircleIcon className="size-3.5 text-green-500" />
                  <span>Auto-detect columns</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircleIcon className="size-3.5 text-green-500" />
                  <span>Preview first 5 rows</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircleIcon className="size-3.5 text-green-500" />
                  <span>Handles 100K+ rows</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Step 1: Field Mapping ───────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Map columns to user fields</h3>
              <span className="text-xs text-muted-foreground">
                {Object.keys(mapping).filter(k => mapping[k]).length} of {columns.length} columns mapped
              </span>
            </div>

            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                      CSV Column
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                      Preview Value
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                      Maps to User Field
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                      Required
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map(col => {
                    const selectedField = mapping[col] || ''
                    const fieldDef = userFields.find(f => f.key === selectedField)
                    const previewVal = previewRows[0]?.[col] || ''

                    return (
                      <tr key={col} className="border-b last:border-0">
                        <td className="px-3 py-2.5 font-medium">{col}</td>
                        <td className="max-w-[200px] truncate px-3 py-2.5 text-muted-foreground">
                          {previewVal || <span className="italic">(empty)</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          <select
                            value={selectedField}
                            onChange={e => setMappingForColumn(col, e.target.value)}
                            className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs outline-none ring-ring focus:ring-1"
                          >
                            <option value="">— Skip column —</option>
                            {userFields.map(f => (
                              <option key={f.key} value={f.key}>
                                {f.label} {f.required ? '*' : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2.5">
                          {fieldDef?.required ? (
                            <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-400">
                              Required
                            </span>
                          ) : selectedField ? (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                              Optional
                            </span>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Description of mapped fields */}
            <div className="mt-3 flex flex-wrap gap-2">
              {userFields.map(f => {
                const mappedCol = Object.entries(mapping).find(([, v]) => v === f.key)?.[0]
                return (
                  <div
                    key={f.key}
                    className={cn(
                      'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px]',
                      mappedCol
                        ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                        : f.required
                          ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                          : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {mappedCol ? (
                      <CheckCircleIcon className="size-3" />
                    ) : (
                      <AlertTriangleIcon className="size-3" />
                    )}
                    <span>{f.label}</span>
                    {mappedCol && (
                      <span className="opacity-70">← {mappedCol}</span>
                    )}
                    {f.required && !mappedCol && <span className="font-semibold">(required)</span>}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Preview of all rows */}
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold">
              Preview ({previewRows.length} of {totalRows.toLocaleString()} rows)
            </h3>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    {columns.map(col => (
                      <th key={col} className="whitespace-nowrap px-2 py-1.5 text-left font-medium text-muted-foreground">
                        {col}
                        {mapping[col] && (
                          <span className="ml-1 text-[10px] text-green-500">
                            → {userFields.find(f => f.key === mapping[col])?.label || mapping[col]}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, i) => (
                    <tr key={i} className="border-b last:border-0">
                      {columns.map(col => (
                        <td key={col} className="max-w-[180px] truncate px-2 py-1.5 text-muted-foreground">
                          {row[col] || <span className="italic opacity-50">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)}>
              <ArrowLeftIcon className="mr-1.5 size-4" />
              Back
            </Button>
            <Button onClick={() => setStep(2)}>
              Continue
              <ArrowRightIcon className="ml-1.5 size-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 2: Global Values ────────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="rounded-xl border bg-card p-6 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <GlobeIcon className="size-5 text-primary" />
              <h3 className="text-base font-semibold">Set global values</h3>
            </div>
            <p className="mb-6 text-sm text-muted-foreground">
              These values are applied to <strong>every row</strong> during import.
              They override any matching values from the CSV file — useful when
              the file doesn't include a column for role or organization.
            </p>

            {isLoadingRefs ? (
              <div className="flex items-center justify-center py-12">
                <LoaderIcon className="size-6 animate-spin text-muted-foreground" />
                <span className="ml-3 text-sm text-muted-foreground">Loading reference data...</span>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2">
                {/* Role */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Default Role
                    <span className="ml-1 text-xs text-muted-foreground">(optional)</span>
                  </label>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Assign a role to all imported users. Leave empty to use the value from
                    each row, or "employee" if not specified.
                  </p>
                  <select
                    value={globalRole}
                    onChange={e => setGlobalRole(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none ring-ring focus:ring-1"
                  >
                    <option value="">— Use row value or default —</option>
                    {importRefs?.roles.map(r => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  {globalRole && (
                    <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                      <CheckCircleIcon className="mr-0.5 inline size-3" />
                      All users will be assigned <strong>{importRefs?.roles.find(r => r.value === globalRole)?.label ?? globalRole}</strong>
                    </p>
                  )}
                </div>

                {/* Organization */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Default Organization
                    <span className="ml-1 text-xs text-muted-foreground">(optional)</span>
                  </label>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Assign all imported users to an organization. Leave empty to use the
                    value from each row, or none if not specified.
                  </p>
                  <select
                    value={String(globalOrgId)}
                    onChange={e => setGlobalOrgId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none ring-ring focus:ring-1"
                  >
                    <option value="">— Use row value or none —</option>
                    {importRefs?.organizations.map(o => (
                      <option key={o.org_id} value={o.org_id}>
                        {o.org_name}
                      </option>
                    ))}
                  </select>
                  {globalOrgId !== '' && (
                    <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                      <CheckCircleIcon className="mr-0.5 inline size-3" />
                      All users will be assigned to{' '}
                      <strong>{importRefs?.organizations.find(o => o.org_id === globalOrgId)?.org_name}</strong>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Summary of what will happen */}
            <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/30 dark:bg-blue-950/20">
              <div className="flex items-start gap-2">
                <GlobeIcon className="mt-0.5 size-4 shrink-0 text-blue-600 dark:text-blue-400" />
                <div className="text-xs text-blue-800 dark:text-blue-300">
                  <p className="font-medium">How global values work:</p>
                  <ul className="mt-1 list-inside list-disc space-y-0.5">
                    <li>Set values apply to <strong>every row</strong> in the import</li>
                    <li>They <strong>override</strong> any matching columns from the CSV file</li>
                    <li>If a CSV column is mapped to the same field, the global value wins</li>
                    <li>If neither the CSV nor a global value is set, defaults are used (role: employee, org: none)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeftIcon className="mr-1.5 size-4" />
              Back
            </Button>
            <Button onClick={handleApplyGlobalValues}>
              Continue
              <ArrowRightIcon className="ml-1.5 size-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 3: Execute confirmation ─────────────────────────────── */}
      {step === 3 && (
        <div className="space-y-4">
          <div className="rounded-xl border bg-card p-6 shadow-sm">
            <h3 className="mb-4 text-base font-semibold">Confirm Import</h3>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="rounded-lg border bg-background p-3 text-center">
                <p className="text-2xl font-bold">{totalRows.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total rows</p>
              </div>
              <div className="rounded-lg border bg-background p-3 text-center">
                <p className="text-2xl font-bold">{columns.length}</p>
                <p className="text-xs text-muted-foreground">Columns detected</p>
              </div>
              <div className="rounded-lg border bg-background p-3 text-center">
                <p className="text-2xl font-bold">
                  {Object.values(mapping).filter(Boolean).length}
                </p>
                <p className="text-xs text-muted-foreground">Fields mapped</p>
              </div>
              <div className="rounded-lg border bg-background p-3 text-center">
                <p className="text-2xl font-bold">
                  {userFields.filter(f => f.required).length}
                </p>
                <p className="text-xs text-muted-foreground">Required fields</p>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium">Mapping summary:</h4>
              {Object.entries(mapping).filter(([, v]) => v).map(([csvCol, userField]) => {
                const label = userFields.find(f => f.key === userField)?.label || userField
                return (
                  <div key={csvCol} className="flex items-center gap-2 text-xs">
                    <CheckCircleIcon className="size-3.5 text-green-500" />
                    <span className="font-medium">{csvCol}</span>
                    <ArrowRightIcon className="size-3 text-muted-foreground" />
                    <span>{label}</span>
                  </div>
                )
              })}
            </div>

            {/* Global values summary */}
            {hasGlobalValues && (
              <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50 p-3 dark:border-purple-900/30 dark:bg-purple-950/20">
                <div className="flex items-start gap-2">
                  <GlobeIcon className="mt-0.5 size-4 shrink-0 text-purple-600 dark:text-purple-400" />
                  <div>
                    <p className="text-xs font-medium text-purple-800 dark:text-purple-300">
                      Global values active:
                    </p>
                    <div className="mt-1 space-y-1">
                      {globalRole && (
                        <p className="text-xs text-purple-700 dark:text-purple-300/80">
                          Role: <strong>{importRefs?.roles.find(r => r.value === globalRole)?.label ?? globalRole}</strong>
                          {' '}(overrides any CSV role column)
                        </p>
                      )}
                      {globalOrgId !== '' && (
                        <p className="text-xs text-purple-700 dark:text-purple-300/80">
                          Organization: <strong>{importRefs?.organizations.find(o => o.org_id === globalOrgId)?.org_name}</strong>
                          {' '}(overrides any CSV org column)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/30 dark:bg-amber-950/20">
              <div className="flex items-start gap-2">
                <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="text-xs text-amber-800 dark:text-amber-300">
                  <p className="font-medium">Before you import:</p>
                  <ul className="mt-1 list-inside list-disc space-y-0.5">
                    <li>Duplicates are automatically skipped (based on email)</li>
                    <li>Auto-generated passwords are set for new users</li>
                    <li>This action is logged in the audit trail</li>
                    <li>Large imports (100K+ rows) may take several minutes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>
              <ArrowLeftIcon className="mr-1.5 size-4" />
              Back
            </Button>
            <Button
              onClick={handleExecute}
              disabled={isExecuting}
              className="min-w-[160px]"
            >
              {isExecuting ? (
                <>
                  <LoaderIcon className="mr-1.5 size-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <DatabaseIcon className="mr-1.5 size-4" />
                  Import {totalRows.toLocaleString()} users
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 4: Results ──────────────────────────────────────────── */}
      {step === 4 && importResult && (
        <div className="space-y-4">
          <div className="rounded-xl border bg-card p-6 shadow-sm">
            <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
              <CheckCircleIcon className="size-5 text-green-500" />
              Import Complete
            </h3>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-center dark:border-green-900/30 dark:bg-green-950/20">
                <CheckCircleIcon className="mx-auto mb-1 size-5 text-green-500" />
                <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                  {importResult.inserted.toLocaleString()}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400/80">Inserted</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center dark:border-amber-900/30 dark:bg-amber-950/20">
                <AlertTriangleIcon className="mx-auto mb-1 size-5 text-amber-500" />
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                  {importResult.skipped.toLocaleString()}
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400/80">Skipped</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center dark:border-red-900/30 dark:bg-red-950/20">
                <XCircleIcon className="mx-auto mb-1 size-5 text-red-500" />
                <p className="text-2xl font-bold text-red-700 dark:text-red-400">
                  {importResult.errors.toLocaleString()}
                </p>
                <p className="text-xs text-red-600 dark:text-red-400/80">Errors</p>
              </div>
              <div className="rounded-lg border bg-background p-3 text-center">
                <UsersIcon className="mx-auto mb-1 size-5 text-muted-foreground" />
                <p className="text-2xl font-bold">{importResult.total_rows.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total processed</p>
              </div>
            </div>

            {/* Error details */}
            {importResult.error_details.length > 0 && (
              <div className="mt-4">
                <h4 className="mb-2 text-sm font-medium text-red-600 dark:text-red-400">
                  Error details ({importResult.error_details.length} rows)
                </h4>
                <div className="max-h-48 overflow-y-auto rounded-lg border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-2 py-1 text-left font-medium text-muted-foreground">Row</th>
                        <th className="px-2 py-1 text-left font-medium text-muted-foreground">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.error_details.slice(0, 50).map((err, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-2 py-1 text-muted-foreground">{err.row}</td>
                          <td className="px-2 py-1 text-red-500">{err.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={resetImport}>
              <UploadIcon className="mr-1.5 size-4" />
              Import another file
            </Button>
            <Button variant="ghost" onClick={() => setShowHistory(true)}>
              <HistoryIcon className="mr-1.5 size-4" />
              View import history
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
